import { normalizeDecimal } from "./decimal";

// --- Nested data access helpers ---

/** Safely access a deeply nested value by dot-separated path */
export function getNestedRawValue(source: unknown, path: string): unknown {
  if (!source || typeof source !== "object") return undefined;
  const keys = path.split(".");
  let current: unknown = source;
  for (const key of keys) {
    if (!current || typeof current !== "object" || !(key in current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/** Try multiple keys on a flat object and return the first non-empty value */
export function getValueFromPaths(
  source: unknown,
  paths: string[],
): unknown {
  for (const path of paths) {
    const value = getNestedRawValue(source, path);
    if (value === undefined || value === null) continue;
    if (typeof value === "string") {
      if (value.trim()) return value;
      continue;
    }
    return value;
  }
  return undefined;
}

// --- Display formatters (raw data → form field value) ---

/**
 * Read a value from `data` using alias keys and normalize it.
 */
export function displayValue(
  data: Record<string, unknown> | undefined,
  aliases: string[],
): string {
  if (!data) return "";
  const val = getValueFromPaths(data, aliases);
  if (val == null) return "";
  return normalizeDecimal(val);
}

/**
 * Normalize a raw value for display in a form field.
 * Returns "" for null/undefined/empty, otherwise the normalized string.
 */
export function displayRaw(value: unknown): string {
  return normalizeDecimal(value) || "";
}

/**
 * Like displayValue but prefixes "+" for positive numbers.
 * Used for signed power fields (SPH, CYL, ADD).
 */
export function displaySigned(
  data: Record<string, unknown> | undefined,
  aliases: string[],
): string {
  const val = displayValue(data, aliases);
  if (!val) return val;
  const num = parseFloat(val);
  if (isNaN(num) || num <= 0) return val;
  return val.startsWith("+") ? val : `+${val}`;
}

/**
 * Return the sign character ("+", "-") for a value, or null if empty.
 * Used for InputAdornment display.
 */
export function getSignPrefix(
  data: Record<string, unknown> | undefined,
  aliases: string[],
): string | null {
  const val = displayValue(data, aliases);
  if (!val) return null;
  const num = parseFloat(val);
  if (isNaN(num)) return null;
  if (num >= 0) return "+";
  return "\u2212";
}

/**
 * Return the absolute numeric value as a string (no sign) for use with sign adornment.
 * Returns "" for empty only.
 */
export function displayAbsValue(
  data: Record<string, unknown> | undefined,
  aliases: string[],
): string {
  const val = displayValue(data, aliases);
  if (!val) return val;
  const num = parseFloat(val);
  if (isNaN(num)) return "";
  return Math.abs(num).toFixed(2);
}

// --- Input helpers ---

/**
 * Snap a numeric string to the nearest 0.25 increment.
 * Returns a two-decimal string (e.g. "1.25", "-0.50")
 * or "" if the input is not a valid number.
 */
export function snapToStep(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "+" || trimmed === "-") return "";
  const num = parseFloat(trimmed);
  if (isNaN(num)) return "";
  const snapped = Math.round(num * 4) / 4;
  const safe = snapped === 0 ? 0 : snapped;
  return safe.toFixed(2);
}

/** Field-specific range limits */
const FIELD_LIMITS: Record<string, { min: number; max: number; integer?: boolean }> = {
  sphere: { min: -19, max: 19 },
  cylinder: { min: -10, max: 0 },
  axis: { min: 0, max: 180, integer: true },
  add: { min: 0, max: 5 },
};

/**
 * Clamp a raw value to the allowed range for a prescription field,
 * snap to 0.25 step (integer for axis), and enforce sign rules.
 * Returns "" for empty/invalid input.
 */
export function clampField(
  field: "sphere" | "cylinder" | "axis" | "add",
  raw: string,
): string {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "+" || trimmed === "-") return "";
  const num = parseFloat(trimmed);
  if (isNaN(num)) return "";

  const limits = FIELD_LIMITS[field];
  if (!limits) return snapToStep(raw);

  let value = num;

  // Enforce sign: CYL must be <= 0, ADD must be >= 0
  if (field === "cylinder" && value > 0) value = 0;
  if (field === "add" && value < 0) value = 0;

  // Clamp to range
  value = Math.max(limits.min, Math.min(limits.max, value));

  // Snap
  if (limits.integer) {
    return String(Math.round(value));
  }
  const snapped = Math.round(value * 4) / 4;
  const safe = snapped === 0 ? 0 : snapped;
  return safe.toFixed(2);
}

/**
 * Filter onChange value for sign-constrained fields.
 * CYL: only allows negative or zero values (strips leading "+")
 * ADD: only allows positive or zero values (strips leading "-")
 * AXIS: only allows digits (no sign, no decimal)
 * Returns the filtered string for immediate state update.
 */
export function filterFieldInput(
  field: "sphere" | "cylinder" | "axis" | "add",
  raw: string,
): string {
  if (field === "axis") {
    // Take only the integer part (before any decimal) then strip non-digits
    // Prevents "90.5" → "905"; correctly yields "90" instead
    return raw.split(".")[0].replace(/[^0-9]/g, "");
  }
  if (field === "cylinder") {
    // Strip leading "+" — allow "-" and digits
    if (raw.startsWith("+")) return raw.slice(1);
  }
  if (field === "add") {
    // Strip leading "-" — allow "+" and digits
    if (raw.startsWith("-")) return raw.slice(1);
  }
  return raw;
}
